import { useState, useEffect } from "react";
import { Alert, Avatar, InputNumber, message, Button } from "antd";
import {
  CheckCircleOutlined,
  EyeOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import GenericStatCard from "../../../../Components/Cards/GenericStatCard";
import garageMoneyIcon from "../../../../Assets/Icons/garage_money.svg";
import laptopCarIcon from "../../../../Assets/Icons/laptop_car.svg";
import { useNavigate } from "react-router-dom";
import GenericTable from "../../../../Components/Tables/GenericTable";
import {
  finalizeCreditSettlement,
  getManagerCredits,
  recalculateCreditAllocation,
} from "../../../../API/FleetManager/CreditsBillings";
import usePageAccess from "../../../../Utlis/usePageAccess";
import { getInitials } from "../../../../Components/InitialsAvatar";

const BACKEND_ORIGIN = process.env.REACT_APP_BACKEND_URL?.replace(
  /\/?api\/?$/,
  "",
);

const resolveAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  let path = avatar.startsWith("/") ? avatar : `/${avatar}`;
  if (!path.startsWith("/media/")) path = `/media${path}`;
  return `${BACKEND_ORIGIN}${path}`;
};

const AVATAR_COLORS = [
  "#4F46E5",
  "#EA580C",
  "#16A34A",
  "#DC2626",
  "#2563EB",
  "#7C3AED",
];
const getAvatarColor = (name) =>
  AVATAR_COLORS[(name || "A").charCodeAt(0) % AVATAR_COLORS.length];

const BADGE_COLORS = [
  { background: "#4f46e51f", color: "#4f46e5" },
  { background: "#fee2e2", color: "#dc2626" },
  { background: "#ecfdf5", color: "#059669" },
  { background: "#ede9fe", color: "#7c3aed" },
  { background: "#f59e0b1f", color: "#f59e0b" },
  { background: "#dbeafe", color: "#3b82f6" },
  { background: "#ea580c1a", color: "#ea580c" },
  { background: "#fdf2f8", color: "#db2777" },
  { background: "#f0fdfa", color: "#0d9488" },
];

const hashString = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
};

const getModuleBadgeStyle = (name) => {
  if (!name || name === "-") {
    return { background: "#f4f4f5", color: "#71717a" };
  }
  if (name.toLowerCase() === "fuel") {
    return { background: "#0000000f", color: "#000000" };
  }
  return BADGE_COLORS[hashString(name) % BADGE_COLORS.length];
};

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (typeof data.message === "string") return data.message;
  if (typeof data.detail === "string") return data.detail;

  const messages = Object.entries(data).flatMap(([field, errors]) => {
    const list = Array.isArray(errors) ? errors : [errors];
    return list.map((err) => `${field.replace(/_/g, " ")}: ${err}`);
  });

  return messages.length ? messages.join(" | ") : fallback;
};

const EqualDistribution = ({ setBtnConfigs }) => {
  const { canEdit } = usePageAccess("credits-billing");
  const navigate = useNavigate();
  const [selectedRow, setSelectedRow] = useState(null);
  const [managerCredits, setManagerCredits] = useState(null);
  const [allocationPreview, setAllocationPreview] = useState(null);
  const [distributeBlock, setDistributeBlock] = useState(null);

  const fetchManagerCredits = async () => {
    try {
      const res = await getManagerCredits();

      if (Math.floor(res?.status / 100) === 2) {
        setManagerCredits(res?.data?.data || null);
      }
    } catch (err) {
      console.error("Failed to load manager credits:", err);
    }
  };

  const fetchAllocationPreview = async () => {
    try {
      const res = await getManagerCredits("equal");

      if (Math.floor(res?.status / 100) === 2) {
        setAllocationPreview(res?.data?.data || null);
        setDistributeBlock(null);
      }
    } catch (err) {
      const data = err?.response?.data;
      if (
        data?.code === "no_credit_balance" ||
        data?.code === "no_active_vehicles"
      ) {
        setAllocationPreview(null);
        setDistributeBlock({ code: data.code, message: data.message });
        return;
      }
      console.error("Failed to load equal allocation preview:", err);
    }
  };

  useEffect(() => {
    fetchManagerCredits();
    fetchAllocationPreview();
  }, []);

  const used = parseFloat(managerCredits?.allocated_total) || 0;
  const limit =
    parseFloat(managerCredits?.total_balance?.value ?? managerCredits?.total_balance) ||
    0;
  const available = parseFloat(managerCredits?.unallocated_balance) || 0;
  const percentageUsed = limit ? (used / limit) * 100 : 0;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const totalFleetCredit = parseFloat(allocationPreview?.wallet_balance) || 0;
  const activeVehiclesCount = managerCredits?.vehicle_count?.value ?? 0;
  const perVehicleAllocation =
    parseFloat(allocationPreview?.rows?.[0]?.amount) || 0;

  const mapAllocationRow = (allocation) => ({
    key: String(allocation.vehicle_id),
    vehicle: allocation.vehicle || "-",
    plateNumber: allocation.license_plate || "-",
    driverName: allocation.driver || "-",
    driverAvatar: resolveAvatarUrl(allocation.driver_avatar),
    module: Array.isArray(allocation.assigned_modules)
      ? allocation.assigned_modules
      : allocation.assigned_modules
        ? [allocation.assigned_modules]
        : [],
    status: allocation.status || "-",
    credits: parseFloat(allocation.amount) || 0,
  });

  const handleRecalculate = async () => {
    message.loading({ content: "Recalculating allocations...", key: "recalc" });

    try {
      const res = await recalculateCreditAllocation("equal");

      if (Math.floor(res?.status / 100) === 2) {
        setAllocationPreview(res?.data?.data || null);

        message.success({
          content: res?.data?.message || "Allocations updated successfully!",
          key: "recalc",
          duration: 2,
        });
      } else {
        message.error({
          content: getErrorMessage(
            res?.data,
            "Failed to recalculate allocations.",
          ),
          key: "recalc",
        });
      }
    } catch (err) {
      message.error({
        content: getErrorMessage(
          err?.response?.data,
          "Failed to recalculate allocations.",
        ),
        key: "recalc",
      });
    }
  };

  const handleView = (record) => {
    navigate("/credit-adjustments-manager", {
      state: { from: "Equal Distribution", vehicleId: record.key },
    });
  };

  const [creditsData, setCreditsData] = useState([]);

  useEffect(() => {
    setCreditsData((allocationPreview?.rows || []).map(mapAllocationRow));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allocationPreview]);

  const creditsColumns = [
    {
      title: "Vehicle",
      dataIndex: "vehicle",
      key: "vehicle",
      enableSearch: "true",
      render: (text) => (
        <span style={{ fontWeight: 700, color: "#111827" }}>{text}</span>
      ),
    },
    {
      title: "Plate Number",
      dataIndex: "plateNumber",
      key: "plateNumber",
      enableSearch: "true",
    },
    {
      title: "Driver",
      dataIndex: "driver",
      key: "driver",
      enableSearch: "true",
      render: (_, record) =>
        record.driverAvatar ? (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar src={record.driverAvatar} size="small" />
            <span style={{ fontSize: "14px", color: "#3F3F46" }}>
              {record.driverName}
            </span>
          </div>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Avatar
              size="small"
              style={{
                background: getAvatarColor(record.driverName),
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              {getInitials(record.driverName)}
            </Avatar>
            <span style={{ fontSize: "14px", color: "#3F3F46" }}>
              {record.driverName}
            </span>
          </div>
        ),
    },
    {
      title: "Assign Module",
      dataIndex: "module",
      key: "module",
      render: (modules) =>
        Array.isArray(modules) && modules.length > 0 ? (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {modules.map((module) => (
              <span
                key={module}
                className="assigned-module-badge"
                style={getModuleBadgeStyle(module)}
              >
                {module}
              </span>
            ))}
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: "Equal Credits",
      dataIndex: "credits",
      key: "assignedCredits",
      sorter: (a, b) => (a.credits || 0) - (b.credits || 0),
      render: (value) => (
        <span style={{ color: "#4F46E5", fontWeight: 500 }}>
          ${Number(value || 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: [...new Set(creditsData.map((item) => item.status))].map(
        (status) => ({
          text: status,
          value: status,
        }),
      ),
      onFilter: (value, record) => record.status === value,
      render: (text) => (
        <span
          className={`status-badge ${text === "Active" ? "active" : "locked"}`}
        >
          {text}
        </span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <EyeOutlined
            style={{
              cursor: "pointer",
              color: "#4F46E5",
              fontSize: 18,
            }}
            onClick={() => handleView(record)}
          />
        </div>
      ),
    },
  ];

  const handleFinalizeSettlement = async () => {
    try {
      const res = await finalizeCreditSettlement("equal");

      if (Math.floor(res?.status / 100) === 2) {
        setAllocationPreview(res?.data?.data || null);
        message.success(res?.data?.message || "Settlement Finalized");
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to finalize settlement."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to finalize settlement.",
        ),
      );
    }
  };

  useEffect(() => {
    setBtnConfigs([
      {
        text: "Finalize Settlement",
        icon: <CheckCircleOutlined />,
        onClick: handleFinalizeSettlement,
        disabled: !!distributeBlock,
        title: distributeBlock?.message,
      },
    ]);
  }, [setBtnConfigs, creditsData, distributeBlock]);

  return (
    <div className="credits-billings-page">
      {distributeBlock && (
        <Alert
          className="distribute-block-alert"
          type="warning"
          showIcon
          message={distributeBlock.message}
        />
      )}
      <div className="row">
        <div className="stats-container">
          <GenericStatCard
            title="Available to Allocate"
            value={`$${available.toFixed(2)}`}
            icon={garageMoneyIcon}
          />
          <GenericStatCard
            title="Total Active Vehicles"
            value={activeVehiclesCount}
            {...(managerCredits?.vehicle_count?.percentage != null && {
              percentage: `${managerCredits.vehicle_count.percentage}`,
            })}
            isIncrease={managerCredits?.vehicle_count?.is_increase}
            icon={laptopCarIcon}
          />
        </div>
      </div>
      <div className="row credit-row">
        <div className="tabs card">
          <div className="credit-card">
            <div className="credit-title">Credit Utilization in JMD</div>

            <div className="credit-bar-section">
              <span className="label-card">Available Credits</span>
              <span className="available-amount">
                ${available.toLocaleString()}
              </span>
            </div>

            <div className="credit-bar">
              <div
                className="credit-bar-fill"
                style={{ width: `${percentageUsed}%` }}
              ></div>
            </div>

            <div className="credit-footer">
              <span>
                Used:<span> ${used.toLocaleString()}</span>
              </span>
              <span>
                Limit: <span>${limit.toLocaleString()}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        className="formula-card"
        style={{
          background: "#ffffff",
          borderRadius: "12px",
          padding: isMobile ? "16px" : "24px",
          border: "1px solid #E4E4E7",
          marginTop: "24px",
          boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.05)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: isMobile ? "flex-start" : "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: isMobile ? "12px" : "0px",
            removeAttribute: "justify-content",
            marginBottom: "16px",
            width: "100%",
          }}
        >
          <div
            style={{
              fontSize: "12px",
              fontWeight: "700",
              color: "#45464D",
              letterSpacing: "0.05em",
              textTransform: "uppercase",
            }}
          >
            Active Distribution Formula
          </div>
          {canEdit && (
            <Button
              type="text"
              icon={<SyncOutlined style={{ color: "#4F46E5" }} />}
              onClick={handleRecalculate}
              disabled={!!distributeBlock}
              style={{
                color: "#4F46E5",
                fontWeight: 600,
                background: "#4F46E53D",
                borderRadius: "8px",
                fontSize: "14px",
                marginLeft: isMobile ? "0px" : "auto",
                width: isMobile ? "100%" : "auto",
                justifyContent: "center",
              }}
            >
              Recalculate
            </Button>
          )}
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            textAlign: isMobile ? "center" : "left",
            gap: isMobile ? "16px" : "24px",
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: isMobile ? "28px" : "32px",
                fontWeight: "600",
                color: "#111827",
              }}
            >
              $
              {totalFleetCredit.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#71717A",
                marginTop: "4px",
                fontWeight: "500",
              }}
            >
              Total Fleet Credit
            </div>
          </div>

          <div
            style={{
              fontSize: "28px",
              color: "#A1A1AA",
              fontWeight: "300",
              transform: isMobile ? "rotate(90deg)" : "none",
              margin: isMobile ? "0 auto" : "0",
            }}
          >
            /
          </div>

          <div>
            <div
              style={{
                fontSize: isMobile ? "28px" : "32px",
                fontWeight: "600",
                color: "#111827",
              }}
            >
              {activeVehiclesCount}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#71717A",
                marginTop: "4px",
                fontWeight: "500",
              }}
            >
              Active Vehicles
            </div>
          </div>

          <div
            style={{
              fontSize: "28px",
              color: "#A1A1AA",
              fontWeight: "300",
              transform: isMobile ? "rotate(90deg)" : "none",
              margin: isMobile ? "0 auto" : "0",
            }}
          >
            =
          </div>

          <div
            style={{
              background: "#4F46E53D",
              borderRadius: "12px",
              padding: "16px 24px",
              textAlign: "center",
              minWidth: isMobile ? "100%" : "180px",
              border: "1px solid #0000001A",
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "28px" : "32px",
                fontWeight: "700",
                color: "#4F46E5",
              }}
            >
              $
              {perVehicleAllocation.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "#6366F1",
                marginTop: "4px",
                fontWeight: "600",
              }}
            >
              Per Vehicle Allocation
            </div>
          </div>
        </div>
      </div>

      <div className="credit-table">
        <GenericTable
          dataSource={creditsData}
          columns={creditsColumns}
          enableRowSelection={false}
        />
      </div>
    </div>
  );
};

export default EqualDistribution;
